import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

function AuthPage() {
  const { user, loading, sendVerificationCode, verifyCode, register, login, googleLogin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) {
      navigate("/app", { replace: true });
    }
  }, [loading, user, navigate]);

  const [searchParams] = useSearchParams();

  const [mode, setMode] = useState<"register" | "login">(() => {
    const modeParam = searchParams.get("mode");
    return modeParam === "login" ? "login" : "register";
  });
  const [regStep, setRegStep] = useState<1 | 2>(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [sendingCode, setSendingCode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  if (loading || user) {
    return (
      <div style={{ display: "grid", minHeight: "100vh", placeItems: "center" }}>
        <div className="loading-spinner" />
      </div>
    );
  }

  function startCountdown(seconds: number) {
    setCountdown(seconds);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (countdownRef.current) clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }

  async function handleSendCode() {
    if (!email.trim()) {
      toast.error("请输入邮箱地址");
      return;
    }
    setSendingCode(true);
    try {
      await sendVerificationCode(email.trim());
      toast.success("验证码已发送，请查收邮件");
      setRegStep(2);
      startCountdown(60);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "发送验证码失败");
    } finally {
      setSendingCode(false);
    }
  }

  async function handleResendCode() {
    if (countdown > 0) return;
    await handleSendCode();
  }

  async function handleRegister() {
    if (!code.trim() || code.length !== 6) {
      toast.error("请输入 6 位验证码");
      return;
    }
    if (!password || password.length < 6) {
      toast.error("密码至少 6 位");
      return;
    }
    setSubmitting(true);
    try {
      const { verifyToken } = await verifyCode(email.trim(), code.trim());
      await register(email.trim(), password, verifyToken);
      toast.success("注册成功，欢迎使用 DeepSearch");
      navigate("/app");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "注册失败");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error("请填写邮箱和密码");
      return;
    }
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success("登录成功");
      navigate("/app");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "登录失败");
    } finally {
      setSubmitting(false);
    }
  }

  function switchMode(newMode: "register" | "login") {
    setMode(newMode);
    setRegStep(1);
    setEmail("");
    setCode("");
    setPassword("");
    setCountdown(0);
    if (countdownRef.current) clearInterval(countdownRef.current);
  }

  return (
    <div className="auth-page">
      {/* Left: Illustration */}
      <div className="auth-illustration">
        <Link to="/" className="auth-illustration-logo">DeepSearch</Link>
        <svg
          className="auth-illustration-svg"
          viewBox="0 0 480 480"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Background decorative circles */}
          <circle cx="240" cy="240" r="200" fill="url(#bg-grad)" opacity="0.15" />
          <circle cx="380" cy="120" r="80" fill="url(#accent-grad)" opacity="0.12" />
          <circle cx="100" cy="360" r="60" fill="url(#accent-grad)" opacity="0.08" />

          {/* Magnifying glass */}
          <g transform="translate(160, 140)">
            <circle cx="64" cy="64" r="56" stroke="url(#primary-grad)" strokeWidth="6" fill="none" />
            <line x1="104" y1="104" x2="148" y2="148" stroke="url(#primary-grad)" strokeWidth="8" strokeLinecap="round" />
          </g>

          {/* Document / research papers */}
          <g transform="translate(260, 240)">
            <rect x="0" y="0" width="100" height="130" rx="8" fill="white" stroke="var(--border-color)" strokeWidth="1.5" />
            <rect x="14" y="20" width="50" height="6" rx="3" fill="var(--primary-color)" opacity="0.6" />
            <rect x="14" y="34" width="72" height="4" rx="2" fill="var(--text-secondary)" opacity="0.3" />
            <rect x="14" y="44" width="60" height="4" rx="2" fill="var(--text-secondary)" opacity="0.3" />
            <rect x="14" y="54" width="68" height="4" rx="2" fill="var(--text-secondary)" opacity="0.3" />
            <rect x="14" y="64" width="40" height="4" rx="2" fill="var(--text-secondary)" opacity="0.3" />
            <rect x="14" y="80" width="55" height="6" rx="3" fill="#7c3aed" opacity="0.4" />
            <rect x="14" y="94" width="70" height="4" rx="2" fill="var(--text-secondary)" opacity="0.3" />
          </g>

          {/* Floating data nodes */}
          <g>
            <circle cx="120" cy="300" r="5" fill="url(#primary-grad)" opacity="0.5" />
            <circle cx="320" cy="160" r="4" fill="url(#primary-grad)" opacity="0.4" />
            <circle cx="360" cy="340" r="6" fill="#7c3aed" opacity="0.35" />
            <circle cx="200" cy="380" r="4" fill="var(--primary-color)" opacity="0.3" />
            <circle cx="80" cy="200" r="3" fill="#7c3aed" opacity="0.4" />
          </g>

          {/* Connecting arcs */}
          <path
            d="M 200 380 Q 260 320 320 160"
            stroke="var(--primary-color)"
            strokeWidth="1.5"
            strokeDasharray="6 4"
            opacity="0.25"
            fill="none"
          />
          <path
            d="M 120 300 Q 180 220 160 196"
            stroke="#7c3aed"
            strokeWidth="1.5"
            strokeDasharray="6 4"
            opacity="0.2"
            fill="none"
          />

          {/* Gradients */}
          <defs>
            <linearGradient id="primary-grad" x1="0" y1="0" x2="1" y2="1">
              <stop stopColor="var(--primary-color)" />
              <stop offset="1" stopColor="#7c3aed" />
            </linearGradient>
            <linearGradient id="accent-grad" x1="0" y1="0" x2="1" y2="1">
              <stop stopColor="var(--primary-color)" />
              <stop offset="1" stopColor="#7c3aed" />
            </linearGradient>
            <radialGradient id="bg-grad" cx="0.5" cy="0.5" r="0.5">
              <stop stopColor="var(--primary-color)" />
              <stop offset="1" stopColor="#7c3aed" />
            </radialGradient>
          </defs>
        </svg>
        <div className="auth-illustration-caption">
          <p className="auth-illustration-title">DeepSearch</p>
          <p className="auth-illustration-desc">AI 驱动的深度研究平台</p>
        </div>
      </div>

      {/* Right: Auth form */}
      <div className="auth-container">
        <div className="auth-header">
          <Link to="/" className="auth-logo">DeepSearch</Link>
          <p className="auth-subtitle">AI 深度研究平台</p>
        </div>

        <div className="auth-tabs">
          <button
            type="button"
            className={`auth-tab ${mode === "register" ? "active" : ""}`}
            onClick={() => switchMode("register")}
          >
            注册
          </button>
          <button
            type="button"
            className={`auth-tab ${mode === "login" ? "active" : ""}`}
            onClick={() => switchMode("login")}
          >
            登录
          </button>
        </div>

        {mode === "register" ? (
          <>
            {regStep === 1 && (
              <div className="auth-form">
                <h2>创建账号</h2>
                <p className="auth-desc">输入邮箱获取验证码</p>
                <div className="form-group">
                  <label htmlFor="reg-email">邮箱</label>
                  <input
                    id="reg-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                  />
                </div>
                <button
                  type="button"
                  className="btn btn-primary btn-full"
                  disabled={sendingCode}
                  onClick={handleSendCode}
                >
                  {sendingCode ? "发送中..." : "获取验证码"}
                </button>
              </div>
            )}

            {regStep === 2 && (
              <div className="auth-form">
                <h2>验证邮箱</h2>
                <p className="auth-desc">验证码已发送至 {email}</p>

                <div className="form-group">
                  <label>验证码</label>
                  <div className="otp-inputs">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <input
                        key={i}
                        type="text"
                        maxLength={1}
                        className="otp-slot"
                        value={code[i] || ""}
                        onChange={(e) => {
                          const newCode = code.split("");
                          newCode[i] = e.target.value.replace(/\D/g, "");
                          setCode(newCode.join("").slice(0, 6));
                          if (e.target.value && i < 5) {
                            const next = e.currentTarget.parentElement?.children[i + 1] as HTMLInputElement;
                            next?.focus();
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Backspace" && !code[i] && i > 0) {
                            const prev = e.currentTarget.parentElement?.children[i - 1] as HTMLInputElement;
                            prev?.focus();
                          }
                        }}
                      />
                    ))}
                  </div>
                  <div className="otp-resend">
                    <button
                      type="button"
                      disabled={countdown > 0}
                      onClick={handleResendCode}
                    >
                      {countdown > 0 ? `${countdown} 秒后重新发送` : "重新发送验证码"}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="reg-password">设置密码</label>
                  <input
                    id="reg-password"
                    type="password"
                    required
                    minLength={6}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="至少 6 位"
                  />
                </div>

                <button
                  type="button"
                  className="btn btn-primary btn-full"
                  disabled={submitting}
                  onClick={handleRegister}
                >
                  {submitting ? "注册中..." : "注册并开始"}
                </button>

                <button
                  type="button"
                  className="btn btn-text btn-full"
                  onClick={() => { setRegStep(1); setCode(""); setPassword(""); }}
                >
                  返回上一步
                </button>
              </div>
            )}
          </>
        ) : (
          <form onSubmit={handleLogin} className="auth-form">
            <h2>欢迎回来</h2>
            <p className="auth-desc">登录继续使用你的工作台</p>
            <div className="form-group">
              <label htmlFor="login-email">邮箱</label>
              <input
                id="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </div>
            <div className="form-group">
              <label htmlFor="login-password">密码</label>
              <input
                id="login-password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="至少 6 位"
              />
            </div>
            <button type="submit" className="btn btn-primary btn-full" disabled={submitting}>
              {submitting ? "登录中..." : "登录"}
            </button>
          </form>
        )}

        <div className="auth-divider">
          <span>或通过以下方式</span>
        </div>
        <button
          type="button"
          className="btn btn-outline btn-full"
          onClick={googleLogin}
        >
          <svg className="google-icon" viewBox="0 0 24 24" width="18" height="18">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Google 登录
        </button>
      </div>
    </div>
  );
}

export default AuthPage;
